import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Stack,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Avatar
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Search as SearchIcon,
  SmartToy as AIIcon,
  AutoAwesome as MagicIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon,
  TrendingUp as DifficultyIcon,
  ListAlt as StepsIcon,
  ContactPhone as ContactIcon,
  Tag as TagIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';
import { apiCall } from '../utils/api';
import AITemplateCreator from './AITemplateCreator';

const TemplateSelector = ({ onSelectTemplate, onBack, familyId, onDeleteTemplate, onViewExistingTemplate, onCreateRunbook }) => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customizations, setCustomizations] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentView, setCurrentView] = useState('templates'); // 'templates' or 'ai-creator'
  const [deleteTemplateDialogOpen, setDeleteTemplateDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const data = await apiCall('/api/templates');
      setTemplates(data);
      setFilteredTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    filterTemplates(query, selectedCategory);
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    filterTemplates(searchQuery, category);
  };

  const filterTemplates = (query, category) => {
    let filtered = templates;

    // Filter by category
    if (category !== 'all') {
      filtered = filtered.filter(template => 
        template.category.toLowerCase() === category.toLowerCase()
      );
    }

    // Filter by search query
    if (query.trim()) {
      const searchTerm = query.toLowerCase();
      filtered = filtered.filter(template =>
        template.title.toLowerCase().includes(searchTerm) ||
        template.description.toLowerCase().includes(searchTerm) ||
        template.category.toLowerCase().includes(searchTerm) ||
        (template.tags && template.tags.some(tag => 
          tag.toLowerCase().includes(searchTerm)
        ))
      );
    }

    setFilteredTemplates(filtered);
  };

  const getUniqueCategories = () => {
    const categories = [...new Set(templates.map(template => template.category))];
    return categories.sort();
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setCustomizations({
      title: template.title,
      description: template.description
    });
  };

  const handleCustomizationChange = (field, value) => {
    setCustomizations(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUseTemplate = () => {
    onSelectTemplate(selectedTemplate, customizations);
  };

  const handleAITemplateCreated = (templateData) => {
    console.log('=== TEMPLATE SELECTOR: AI template created callback triggered ===');
    console.log('Template data received:', templateData);
    
    try {
      console.log('=== TEMPLATE SELECTOR: About to refresh templates ===');
      // Refresh templates list to include AI generated templates
      fetchTemplates();
      console.log('=== TEMPLATE SELECTOR: Templates refreshed successfully ===');
      
      console.log('=== TEMPLATE SELECTOR: About to change view to templates ===');
      // Go back to template list view
      setCurrentView('templates');
      console.log('=== TEMPLATE SELECTOR: View changed to templates ===');
      
      // Call the parent callback to refresh the runbooks list
      if (onSelectTemplate) {
        console.log('=== TEMPLATE SELECTOR: About to call parent onSelectTemplate ===');
        // If we have template data, pass it properly; otherwise signal refresh
        if (templateData) {
          console.log('=== TEMPLATE SELECTOR: Calling onSelectTemplate with template data ===');
          onSelectTemplate(templateData, {}); // Pass template with empty customizations
        } else {
          console.log('=== TEMPLATE SELECTOR: Calling onSelectTemplate with refresh signal ===');
          onSelectTemplate(null, null); // Signal that template was created
        }
        console.log('=== TEMPLATE SELECTOR: Parent callback completed ===');
      } else {
        console.log('=== TEMPLATE SELECTOR: No parent onSelectTemplate callback ===');
      }
      
      console.log('=== TEMPLATE SELECTOR: AI template created callback completed successfully ===');
    } catch (error) {
      console.error('=== TEMPLATE SELECTOR: Error in handleAITemplateCreated ===', error);
      // Continue with the flow even if there's an error
    }
  };

  const handleBackFromAI = () => {
    setCurrentView('templates');
  };

  const handleDeleteTemplate = (e, template) => {
    e.stopPropagation(); // Prevent template selection
    setTemplateToDelete(template);
    setDeleteTemplateDialogOpen(true);
  };

  const handleDeleteTemplateConfirm = async () => {
    try {
      if (onDeleteTemplate && templateToDelete) {
        await onDeleteTemplate(templateToDelete.id);
        // Refresh templates after deletion
        await fetchTemplates();
        setDeleteTemplateDialogOpen(false);
        setTemplateToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      // Use setError instead of alert if available, or keep console.error
    }
  };

  const handleDeleteTemplateCancel = () => {
    setDeleteTemplateDialogOpen(false);
    setTemplateToDelete(null);
  };

  const getDifficultyColor = (level) => {
    switch (level) {
      case 'beginner': return 'success';
      case 'intermediate': return 'warning';
      case 'advanced': return 'error';
      default: return 'default';
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Loading templates...
          </Typography>
        </Box>
      </Container>
    );
  }

  // AI Template Creator View
  if (currentView === 'ai-creator') {
    return (
      <AITemplateCreator
        familyId={familyId}
        onTemplateCreated={handleAITemplateCreated}
        onBack={handleBackFromAI}
        onViewExistingTemplate={onViewExistingTemplate}
      />
    );
  }

  if (selectedTemplate) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <IconButton onClick={() => setSelectedTemplate(null)} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              Customize Template
            </Typography>
          </Box>

          {/* Template Preview */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                {selectedTemplate.title}
              </Typography>
              <Chip label={selectedTemplate.category} color="primary" variant="outlined" sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Chip
                  icon={<DifficultyIcon />}
                  label={selectedTemplate.difficulty_level}
                  color={getDifficultyColor(selectedTemplate.difficulty_level)}
                  size="small"
                />
                {selectedTemplate.estimated_time && (
                  <Chip
                    icon={<TimeIcon />}
                    label={selectedTemplate.estimated_time}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Customization Form */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Customize Template
              </Typography>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Title"
                  value={customizations.title || ''}
                  onChange={(e) => handleCustomizationChange('title', e.target.value)}
                  placeholder="Customize the title"
                />

                <TextField
                  fullWidth
                  label="Description"
                  value={customizations.description || ''}
                  onChange={(e) => handleCustomizationChange('description', e.target.value)}
                  placeholder="Customize the description"
                  multiline
                  rows={3}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Template Steps Preview */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Steps Preview
              </Typography>
              <List>
                {selectedTemplate.steps.map((step, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Paper
                        sx={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.875rem'
                        }}
                      >
                        {index + 1}
                      </Paper>
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        typeof step === 'object' && step !== null ? (
                          <Box>
                            {step.title && (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {step.title}
                              </Typography>
                            )}
                            {step.description && (
                              <Typography variant="body2">
                                {step.description}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            {String(step)}
                          </Typography>
                        )
                      } 
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Emergency Contacts */}
          {selectedTemplate.emergency_contacts && selectedTemplate.emergency_contacts.length > 0 && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Emergency Contacts
                </Typography>
                <Stack spacing={1}>
                  {selectedTemplate.emergency_contacts.map((contact, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="body2">
                        <strong>{contact.name}</strong>: {contact.phone}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={onBack}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleUseTemplate}>
              Use This Template
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={onBack} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Choose a Template
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Start with a pre-built runbook template and customize it for your family
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* AI Creation Option */}
        <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ mr: 2, bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <AIIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" component="h3" gutterBottom>
                    Create with AI
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Describe what you need in plain English and let AI generate a custom template for you
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<MagicIcon />}
                onClick={() => setCurrentView('ai-creator')}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
              >
                Generate Template
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Create New Runbook Option */}
        <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ mr: 2, bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <StepsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" component="h3" gutterBottom>
                    Create New Runbook
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Start from scratch and build a custom runbook tailored to your family's specific needs
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<ArrowIcon />}
                onClick={() => onCreateRunbook ? onCreateRunbook() : onBack()}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
              >
                Create Runbook
              </Button>
            </Box>
          </CardContent>
        </Card>

        {templates.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" component="h3" gutterBottom color="text.secondary">
              No templates available
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Templates will appear here once they are created by administrators.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Search and Filters */}
            <Box sx={{ mb: 4 }}>
              <TextField
                fullWidth
                placeholder="Search templates by title, description, category, or tags..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />
              
              <ToggleButtonGroup
                value={selectedCategory}
                exclusive
                onChange={(e, newCategory) => newCategory && handleCategoryFilter(newCategory)}
                sx={{ flexWrap: 'wrap' }}
              >
                <ToggleButton value="all">
                  All ({templates.length})
                </ToggleButton>
                {getUniqueCategories().map(category => {
                  const count = templates.filter(t => t.category === category).length;
                  return (
                    <ToggleButton key={category} value={category}>
                      {category} ({count})
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            </Box>

            {/* Search Results */}
            <Box>
              {filteredTemplates.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" component="h3" gutterBottom color="text.secondary">
                    No templates found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your search terms or category filter.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Showing {filteredTemplates.length} of {templates.length} templates
                    {searchQuery && ` for "${searchQuery}"`}
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {filteredTemplates.map(template => (
                      <Grid item xs={12} sm={6} md={4} key={template.id}>
                        <Card 
                          sx={{ 
                            height: '100%',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 4,
                            },
                          }}
                          onClick={() => handleTemplateSelect(template)}
                        >
                          <CardContent sx={{ pb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{ flex: 1, mr: 2 }}>
                                <Typography variant="h6" component="h3" gutterBottom>
                                  {template.title}
                                </Typography>
                                <Chip label={template.category} size="small" variant="outlined" />
                              </Box>
                              <IconButton
                                size="small"
                                onClick={(e) => handleDeleteTemplate(e, template)}
                                color="error"
                                title="Delete template"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                            
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ mb: 2, lineHeight: 1.5 }}
                            >
                              {template.description}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                              <Chip
                                icon={<DifficultyIcon />}
                                label={template.difficulty_level}
                                color={getDifficultyColor(template.difficulty_level)}
                                size="small"
                              />
                              {template.estimated_time && (
                                <Chip
                                  icon={<TimeIcon />}
                                  label={template.estimated_time}
                                  variant="outlined"
                                  size="small"
                                />
                              )}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                              <Chip
                                icon={<StepsIcon />}
                                label={`${template.steps.length} steps`}
                                size="small"
                                variant="outlined"
                              />
                              {template.emergency_contacts && template.emergency_contacts.length > 0 && (
                                <Chip
                                  icon={<ContactIcon />}
                                  label={`${template.emergency_contacts.length} contacts`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>

                            {template.tags && template.tags.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                  {template.tags.map(tag => (
                                    <Chip
                                      key={tag}
                                      label={tag}
                                      size="small"
                                      variant="outlined"
                                      icon={<TagIcon />}
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </CardContent>
                          
                          <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', width: '100%', justifyContent: 'center' }}>
                              <Typography variant="body2" sx={{ mr: 0.5 }}>
                                Use Template
                              </Typography>
                              <ArrowIcon fontSize="small" />
                            </Box>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Delete Template Confirmation Dialog */}
      <Dialog
        open={deleteTemplateDialogOpen}
        onClose={handleDeleteTemplateCancel}
        aria-labelledby="delete-template-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-template-dialog-title">
          Delete Template
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete template "{templateToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleDeleteTemplateCancel} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteTemplateConfirm} 
            variant="contained" 
            color="error"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TemplateSelector;